/**
 * AVISOS GLOBALES DE CONECTIVIDAD Y ACTUALIZACIÓN — FASE 4.5 (+ F14.1)
 *
 * Se monta UNA vez, fuera de `<Routes>` (ver `App.tsx`): así se ve igual
 * en `/entrar` (sin `Shell`) que en cualquier pantalla autenticada, sin
 * duplicar la lógica en las dos.
 *
 * F14.1:
 *   · la conexión es la REAL (`pwa.ts`): «Sin conexión» (sin red) se distingue de «Sin conexión con el servidor»
 *     (hay red pero NeatOS no responde: wifi sin salida, portal cautivo, servidor caído);
 *   · «Actualizar ahora» nunca recarga a ciegas si hay cambios guardados en este dispositivo: lo dice antes (no se
 *     pierden: viven en IndexedDB), y recuerda guardar lo que se esté rellenando.
 */
import { useState, type ReactNode } from 'react';

import { useSesionOpcional } from '../contexto.tsx';
import { useActualizacionPwa, useConectividad, useConexionReal } from '../pwa.ts';
import { contarSinSincronizarDe } from './indicador-sync.tsx';

export function EstadoConexion(): ReactNode {
  const conectividad = useConectividad();
  const conexion = useConexionReal();
  const { disponible, actualizar } = useActualizacionPwa();
  const usuario = useSesionOpcional()?.usuario ?? null;
  //  El recuento se hace AL PULSAR (no con un valor que pudiera estar desfasado): `null` = sin confirmar.
  const [enCola, setEnCola] = useState<number | null>(null);
  const confirmando = enCola !== null;
  function pedirActualizacion(): void {
    void contarSinSincronizarDe(usuario).then((n) => (n > 0 ? setEnCola(n) : actualizar()));
  }

  //  Nunca `null` a secas: este `<div>` es el elemento MÁS ARRIBA de toda
  //  la app (fuera de `<Routes>`) y lleva el `padding-top: env(safe-area-
  //  inset-top)` — si desapareciera del todo sin avisos que mostrar, la
  //  cabecera de debajo quedaría sin ese margen justo cuando no hace
  //  falta pintar nada encima, que es el caso normal la mayor parte del
  //  tiempo.
  return (
    <div className="avisos-globales">
      {conectividad === 'OFFLINE' && (
        <div className="aviso-global aviso-global-offline" role="status">
          {conexion === 'SERVIDOR_NO_DISPONIBLE'
            ? 'Sin conexión con el servidor. Lo que hagas sin conexión se guarda en este dispositivo.'
            : 'Sin conexión. Algunas funciones no están disponibles.'}
        </div>
      )}
      {disponible && !confirmando && (
        <div className="aviso-global aviso-global-actualizacion" role="status">
          <span>Hay una nueva versión de NeatOS disponible.</span>
          <button type="button" className="pequeno" onClick={pedirActualizacion}>
            Actualizar ahora
          </button>
        </div>
      )}
      {disponible && confirmando && (
        <div className="aviso-global aviso-global-actualizacion" role="alertdialog" aria-label="Actualizar con cambios pendientes">
          <span>
            Tienes {enCola} {enCola === 1 ? 'cambio guardado' : 'cambios guardados'} en este dispositivo sin sincronizar: no se pierden al actualizar.
            Si estás rellenando algo, guárdalo antes.
          </span>
          <button type="button" className="pequeno" onClick={actualizar}>Actualizar ahora</button>
          <button type="button" className="pequeno" onClick={() => setEnCola(null)}>Más tarde</button>
        </div>
      )}
    </div>
  );
}
